/**
 * Employee Web — `/login` route.
 *
 * Validates search params (`?redirect=` + `?reason=`) with Zod so bad input
 * surfaces as a route error boundary rather than leaking into downstream
 * code. Delegates to the LoginPage feature wrapper which owns OTP step
 * transition + force-logout alert rendering + deep-link preservation.
 */
import type { JSX } from "react";
import {
  createFileRoute,
  defaultParseSearch,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { safeRedirectPath } from "@linkhr/auth";
import { z } from "zod";
import { LoginPage } from "../features/auth/LoginPage";

const searchSchema = z.object({
  redirect: z.string().optional(),
  // `password_changed`: a temporary password was replaced but the session
  // couldn't carry on; sign in with the new one.
  reason: z.enum(["session_revoked", "password_changed", "workspace_closed"]).optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  // Someone already signed in who opens this page (a bookmark, a typed
  // address) goes straight in. Cannot loop with `requireAuth`: both read the
  // same snapshot and act on opposite answers.
  beforeLoad: ({ context, search }) => {
    if (!context.auth.getSnapshot().isAuthenticated) return;
    const to = safeRedirectPath(search.redirect);
    const qIdx = to.indexOf("?");
    // The router's OWN parser, as the success navigation below uses: a
    // hand-built { key: string } map re-stringifies "?page=2" as `page="2"`
    // and the destination's schema rejects it.
    const parsed = qIdx === -1 ? undefined : defaultParseSearch(to.slice(qIdx));
    throw redirect({
      to: qIdx === -1 ? to : to.slice(0, qIdx),
      ...(parsed !== undefined && Object.keys(parsed).length > 0 ? { search: parsed } : {}),
      replace: true,
    });
  },
  component: LoginRouteComponent,
});

function LoginRouteComponent(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  return (
    <LoginPage
      redirect={search.redirect}
      reason={search.reason}
      onSuccess={(to) => {
        // `to` is the ORIGINAL target the guard captured — a ROUTER-INTERNAL
        // path with the Vite base already EXCLUDED (requireAuth reads
        // `location.pathname`; AuthProvider/createAppRoot use
        // `currentRouterPath()`). Restore it via `navigate({ to })` so the
        // router re-prepends its own basepath ("/me") exactly once: a legit
        // "/me/punch" route becomes the browser URL "/me/me/punch" (correct),
        // NOT "/me/punch" (outside the base → 404).
        //
        // The previous `stripBasePath(to)` was the bug — it stripped the legit
        // leading "/me" route segment from an already-base-excluded path, so
        // the router re-prepended the base onto a truncated path and 404'd.
        //
        // Parse the raw query string through the ROUTER'S OWN `parseSearch`
        // (not a hand-rolled { key: string } object) so values round-trip
        // through `stringifySearch` verbatim: "?mock=1" stays "mock=1" instead
        // of JSON-quoting a string "1" into "mock=%221%22".
        const qIdx = to.indexOf("?");
        const path = qIdx === -1 ? to : to.slice(0, qIdx);
        const rawQuery = qIdx === -1 ? "" : to.slice(qIdx);
        const parsedSearch = rawQuery.length > 0 ? router.options.parseSearch(rawQuery) : undefined;
        void navigate({
          to: path,
          ...(parsedSearch && Object.keys(parsedSearch).length > 0 ? { search: parsedSearch } : {}),
        });
      }}
      onForgotPassword={() => {
        void navigate({ to: "/auth/forgot" });
      }}
    />
  );
}
